import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';

export default function Contact() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    subject: '',
    message: '',
    website: '' // Honeypot field
  });
  
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // إزالة رسالة الخطأ عند بدء الكتابة
    if (submitError) setSubmitError('');
  };

  // فحص معدل الحد المحلي
  const checkLocalRateLimit = (): boolean => {
    const lastSubmit = localStorage.getItem('contact_last_submit');
    if (lastSubmit) {
      const timeDiff = Date.now() - parseInt(lastSubmit);
      if (timeDiff < 90000) { // 90 ثانية
        const remainingTime = Math.ceil((90000 - timeDiff) / 1000);
        setSubmitError(`يرجى الانتظار ${remainingTime} ثانية قبل إرسال رسالة أخرى`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // التحقق من معدل الحد المحلي
    if (!checkLocalRateLimit()) {
      return;
    }
    
    if (!formData.fullName || !formData.email || !formData.subject || !formData.message) {
      setSubmitError('جميع الحقول المطلوبة يجب ملؤها');
      return;
    }
    
    if (formData.message.length > 500) {
      setSubmitError('يجب أن تكون الرسالة أقل من 500 حرف');
      return;
    }

    // التحقق من صحة الإيميل
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitError('يرجى إدخال بريد إلكتروني صحيح');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      const response = await fetch('https://vipatradingco.com/forms/contact-ar.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.fullName,
          email: formData.email,
          company: formData.company,
          subject: formData.subject,
          message: formData.message,
          website: '' // Honeypot
        })
      });
      
      // التحقق من استجابة الخادم
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.ok) {
        // حفظ وقت آخر إرسال
        localStorage.setItem('contact_last_submit', Date.now().toString());
        
        setIsSubmitted(true);
        setFormData({
          fullName: '',
          email: '',
          company: '',
          subject: '',
          message: '',
          website: ''
        });
      } else {
        setSubmitError(result.msg || 'تعذر الإرسال حالياً. حاول لاحقاً أو راسلنا على info@vipatradingco.com');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError('تعذر الإرسال حالياً. تحقق من اتصال الإنترنت أو راسلنا على info@vipatradingco.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" dir="rtl">
      <Header />
      
      {/* Breadcrumb */}
      <div className="bg-gray-50 py-4">
        <div className="container mx-auto px-4">
          <nav className="flex items-center text-sm text-gray-600">
            <Link to="/" className="hover:text-[#E71B25] transition-colors">
              الرئيسية
            </Link>
            <i className="ri-arrow-left-s-line mx-2"></i>
            <span className="text-[#595A5C] font-medium">تواصل معنا</span>
          </nav>
        </div>
      </div>
      
      <main className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h1 className="text-5xl font-bold text-[#595A5C] mb-6">تواصل معنا</h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                نحن هنا للإجابة على استفساراتكم وتقديم المساعدة. تواصلوا معنا وسنرد عليكم في أقرب وقت ممكن
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div>
                <Card padding="lg" className="h-full">
                  <h2 className="text-3xl font-bold text-[#595A5C] mb-8">معلومات التواصل</h2>
                  
                  <div className="space-y-8">
                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-[#E71B25] rounded-lg flex items-center justify-center ml-4 mt-1">
                        <i className="ri-mail-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-[#595A5C] mb-2">البريد الإلكتروني</h3>
                        <p className="text-gray-600">
                          <a href="mailto:info@vipatradingco.com" className="hover:text-[#E71B25] transition-colors">
                            info@vipatradingco.com
                          </a>
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-[#E71B25] rounded-lg flex items-center justify-center ml-4 mt-1">
                        <i className="ri-time-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-[#595A5C] mb-2">وقت الاستجابة</h3>
                        <p className="text-gray-600">
                          سنرد على رسالتكم خلال 1-2 يوم عمل
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-[#E71B25] rounded-lg flex items-center justify-center ml-4 mt-1">
                        <i className="ri-building-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-[#595A5C] mb-2">شركة فيبا التجارية</h3>
                        <p className="text-gray-600">
                          شركة كويتية متعددة الأنشطة متخصصة في البنية التحتية والتطوير العمراني
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-12 p-6 bg-gray-50 rounded-lg">
                    <h3 className="text-xl font-bold text-[#595A5C] mb-4">الشهادات والمعايير</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center">
                        <i className="ri-medal-line text-2xl text-[#E71B25] mb-2"></i>
                        <p className="text-sm font-medium">ISO 9001</p>
                      </div>
                      <div className="text-center">
                        <i className="ri-shield-check-line text-2xl text-[#E71B25] mb-2"></i>
                        <p className="text-sm font-medium">HSE</p>
                      </div>
                      <div className="text-center">
                        <i className="ri-oil-line text-2xl text-[#E71B25] mb-2"></i>
                        <p className="text-sm font-medium">API</p>
                      </div>
                      <div className="text-center">
                        <i className="ri-bank-line text-2xl text-[#E71B25] mb-2"></i>
                        <p className="text-sm font-medium">AML</p>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
              
              {/* Contact Form */}
              <div>
                <Card padding="lg" className="h-full">
                  {isSubmitted ? (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <i className="ri-check-line text-3xl text-green-600"></i>
                      </div>
                      <h2 className="text-3xl font-bold text-green-600 mb-4">تم إرسال الرسالة بنجاح!</h2>
                      <p className="text-lg text-gray-600 mb-8">
                        تم إرسال رسالتك بنجاح. سنعاود التواصل خلال 1–2 يوم عمل.
                      </p>
                      <Button 
                        variant="primary" 
                        onClick={() => setIsSubmitted(false)}
                      >
                        إرسال رسالة أخرى
                      </Button>
                    </div>
                  ) : (
                    <>
                      <h2 className="text-3xl font-bold text-[#595A5C] mb-8">أرسل لنا رسالة</h2>
                      
                      {submitError && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-center">
                            <i className="ri-error-warning-line text-red-600 text-xl ml-3"></i>
                            <p className="text-red-600 font-medium">{submitError}</p>
                          </div>
                        </div>
                      )}
                      
                      <form id="contact-form" onSubmit={handleSubmit} className="space-y-6">
                        {/* Honeypot field - hidden from users */}
                        <input
                          type="text"
                          name="website"
                          value={formData.website}
                          onChange={handleInputChange}
                          style={{ display: 'none' }}
                          tabIndex={-1}
                          autoComplete="off"
                        />
                        
                        <div>
                          <label htmlFor="fullName" className="block text-lg font-medium text-[#595A5C] mb-2">
                            الاسم الكامل *
                          </label>
                          <input
                            type="text"
                            id="fullName"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleInputChange}
                            required
                            disabled={isSubmitting}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E71B25] focus:border-transparent text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                            placeholder="أدخل اسمك الكامل"
                          />
                        </div>
                        
                        <div>
                          <label htmlFor="email" className="block text-lg font-medium text-[#595A5C] mb-2">
                            البريد الإلكتروني *
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            disabled={isSubmitting}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E71B25] focus:border-transparent text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                            placeholder="أدخل بريدك الإلكتروني"
                          />
                        </div>
                        
                        <div>
                          <label htmlFor="company" className="block text-lg font-medium text-[#595A5C] mb-2">
                            الشركة (اختياري)
                          </label>
                          <input
                            type="text"
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleInputChange}
                            disabled={isSubmitting}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E71B25] focus:border-transparent text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                            placeholder="أدخل اسم الشركة"
                          />
                        </div>
                        
                        <div>
                          <label htmlFor="subject" className="block text-lg font-medium text-[#595A5C] mb-2">
                            الموضوع *
                          </label>
                          <input
                            type="text"
                            id="subject"
                            name="subject"
                            value={formData.subject}
                            onChange={handleInputChange}
                            required
                            disabled={isSubmitting}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E71B25] focus:border-transparent text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                            placeholder="أدخل موضوع الرسالة"
                          />
                        </div>
                        
                        <div>
                          <label htmlFor="message" className="block text-lg font-medium text-[#595A5C] mb-2">
                            الرسالة * (حد أقصى 500 حرف)
                          </label>
                          <textarea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            required
                            maxLength={500}
                            rows={6}
                            disabled={isSubmitting}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E71B25] focus:border-transparent text-lg resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                            placeholder="اكتب رسالتك هنا..."
                          />
                          <p className="text-sm text-gray-500 mt-1">
                            {formData.message.length}/500 حرف
                          </p>
                        </div>
                        
                        <Button 
                          type="submit" 
                          variant="primary" 
                          size="lg" 
                          className="w-full"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <i className="ri-loader-4-line animate-spin ml-2"></i>
                              جاري الإرسال...
                            </>
                          ) : (
                            <>
                              إرسال الرسالة
                              <i className="ri-send-plane-line mr-2"></i>
                            </>
                          )}
                        </Button>
                      </form>
                    </>
                  )}
                </Card>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}